/** `formation-shooter/stolen-wings` — the formation-shooter family template
 * authored from the stored Stolen Wings concept: a portrait starfield where a
 * lone fighter, locked to a lane near the bottom, fires up at a swarm that
 * swoops in along curved entry paths and settles into a swaying grid. Bees
 * and moths peel off in weaving dives that fire only on screen; two-hit boss
 * guardians head the grid and dive with a telegraphed tractor beam that
 * captures the fighter instead of destroying it. Shoot the carrier to free the
 * captive — it docks as a double fighter (twin shots, a wider target); if the
 * carrier escapes off the bottom a life is lost. Every third wave is a no-fire
 * bonus formation that loops the screen for points (1000 for a PERFECT clear).
 * Dives speed up 15% a wave, sorties come faster and divers fire more often.
 *
 * The concept's terminal objective is kept: clear wave 5, the flagship wave,
 * to win (the live game had drifted to endless); losing all three fighters is
 * defeat. Constants follow the live `stolen-wings` sim where it matched. */
import type { GameSpecV1, ModuleUseV1 } from '../spec.js';
import type { DirectorConfig, PhaseDef, SpawnGroup } from '../modules/director.js';
import type { TemplateV1 } from './template.js';

const W = 9, H = 16;
const SHIP = 0.9;
/** The fighter's lane (feet), above the phone's bottom control tray. */
const LANE = 13.4 + SHIP / 2;
const COLS = 4;

/** One wave of the grid: `rows` rows of four, `bosses` guardians centred in
 * row 0, bees in the rest of the even rows and moths in the odd rows. Entry
 * groups alternate sides; guardians enter first so they take the head. */
function wave(rows: number, bosses: number): SpawnGroup[] {
  const evenRows = Math.ceil(rows / 2), oddRows = Math.floor(rows / 2);
  const bees = evenRows * COLS - bosses, moths = oddRows * COLS;
  const group = (archetype: string, count: number, left: boolean, delayTicks: number): SpawnGroup =>
    ({ archetype, count, via: 'point', x: left ? -1 : W + 1, y: -0.5, dir: left ? 1 : -1, delayTicks, everyTicks: 5, jitterTicks: 0 });
  return [
    ...(bosses ? [group('boss-guardian', bosses, true, 0)] : []),
    group('bee', bees, false, 12),
    group('moth', moths, true, 24 + 5 * Math.max(bosses, 1)),
  ];
}

/** Live wave growth (4 + ⌊(w−1)/2⌋ rows, 2 + ⌊(w−1)/4⌋ guardians) over the
 * concept's five waves; wave 3 is the bonus formation. */
export function wingsWaveTable(): PhaseDef[] {
  return [1, 2, 3, 4, 5].map(n => {
    const rows = 4 + Math.floor((n - 1) / 2);
    if (n % 3 === 0) return { kind: 'bonus', spawns: wave(4, 0), durationTicks: 660, clearBonus: 1000, layout: [] };
    return { kind: 'clear', spawns: wave(rows, 2 + Math.floor((n - 1) / 4)), durationTicks: 0, clearBonus: 0, layout: [] };
  });
}

const enemy = (role: string, palette: number, hp: number, score: number, tags: string[] = []): GameSpecV1['archetypes'][string] => ({
  role, tags: ['enemy', 'required', 'swarm', ...tags], size: { w: SHIP, h: SHIP },
  render: { palette, depth: 10, anchor: 'center', facing: 'symmetric' },
  modules: [
    { use: 'ai.diver', config: { launch: 'formation', entryTicks: 45, entryArc: 1.6, speed: 5, speedScalar: 'dive.speed', weaveAmp: 2.2, weaveRate: 2.5,
      exit: 'return', holdFireScalar: 'phase.bonus', armMargin: 1 } },
    { use: 'weapon.gun', config: { projectile: 'enemy-shot', speed: 6, cooldownTicks: 1, aim: 'down', trigger: 'auto', autoEveryTicks: 22, maxAlive: 2,
      muzzleY: 0, armTag: 'armed', everyScalar: 'dive.fireEvery' } },
    { use: 'rule.destructible', config: { hp, score, hitTicks: hp > 1 ? 8 : 0 } },
  ],
});

const shot = (role: string, tag: string, palette: number, w: number, h: number): GameSpecV1['archetypes'][string] => ({
  role, tags: [tag], size: { w, h }, render: { palette, depth: 12, anchor: 'center', facing: 'symmetric' },
  modules: [{ use: 'body.ballistic', config: { gravity: 0, lifeTicks: 0, landOnSurfaces: false, leaveTag: tag } }],
});

const playerGun = (): ModuleUseV1 => ({ use: 'weapon.gun', config: { projectile: 'player-shot', speed: 15, cooldownTicks: 8, aim: 'up', trigger: 'a',
  autoEveryTicks: 1, maxAlive: 4, muzzleY: SHIP } });

const director: DirectorConfig = { phases: wingsWaveTable(), loopFrom: -1, interPhaseTicks: 60, requiredTag: 'required', collectTag: 'swarm', sweepTag: 'swarm' };

const globals: ModuleUseV1[] = [
  { use: 'ai.formation', config: { cols: COLS, colGap: 1.5, rowGap: 1.1, top: 2 + SHIP / 2, swayAmp: 0.8, swayPeriodTicks: 314,
    ranks: [{ archetype: 'boss-guardian', rows: [0] }, { archetype: 'bee', rows: [0, 2, 4] }, { archetype: 'moth', rows: [1, 3, 5] }],
    sortieEveryTicks: 55, sortieScalar: 'sortie.every', rushScalar: 'phase.bonus', rushEveryTicks: 20, maxAway: 4, preferTag: 'captor', preferChance: 0.5, leadSortie: true } },
  { use: 'ai.captor', config: { captorTag: 'captor', beam: 'tractor-beam', beamActiveTag: 'tractor', captive: 'captive-fighter', targetTag: 'player',
    beamChance: 1, trackSpeed: 1.6, carryMul: 0.6, parkTicks: 18, dockOffset: SHIP, rescueScore: 1000 } },
  { use: 'rule.contactDamage', key: 'shots', config: { attackerTag: 'player-fire', victimTag: 'enemy', exceptAttackerTags: [], openingSafeTicks: 0, consumeAttacker: true } },
  { use: 'rule.contactDamage', key: 'enemy-fire', config: { attackerTag: 'enemy-fire', victimTag: 'ship', exceptAttackerTags: [], openingSafeTicks: 0, consumeAttacker: true } },
  { use: 'rule.contactDamage', key: 'rams', config: { attackerTag: 'diving', victimTag: 'ship', exceptAttackerTags: ['carrying', 'beaming'], openingSafeTicks: 0, consumeAttacker: false } },
  { use: 'flow.director', config: director },
  // Live escalation from wave 2: dives 15% faster a wave, sorties and diver
  // fire more often (the concept's "no upper limit" is moot over five waves).
  { use: 'flow.escalation', config: { curves: [
    { scalar: 'dive.speed', index: 'phase.number', kind: 'geometric', base: 1, rate: 1.15, min: 1, max: 4 },
    { scalar: 'sortie.every', index: 'phase.number', kind: 'linear', base: 55, rate: -4, min: 18, max: 55 },
    { scalar: 'dive.fireEvery', index: 'phase.number', kind: 'linear', base: 22, rate: -3, min: 10, max: 22 },
  ] } },
  // The concept's decisive end: clear wave 5, the flagship wave, to win.
  { use: 'flow.objective', config: { win: 'phases-cleared', count: 5, milestone: 0, lose: 'players-out', requiredTag: 'required', collectTag: 'swarm', celebrate: true } },
];

export const STOLEN_WINGS_SPEC: GameSpecV1 = {
  schema: 'game-spec@1', kit: 'compose@1',
  title: 'Stolen Wings',
  summary: 'a vertical fixed-screen arcade space shooter against a swooping alien swarm',
  world: { w: W, h: H, orientation: 'portrait' },
  players: { archetypes: ['star-fighter'], spawns: [{ x: W / 2, y: LANE, facing: 1 }] },
  archetypes: {
    'star-fighter': {
      role: 'a sleek cyan arrowhead star-fighter with twin wing cannons and a glowing engine',
      tags: ['player', 'ship'], size: { w: SHIP, h: SHIP }, render: { palette: 2, depth: 20, anchor: 'center', facing: 'symmetric' },
      modules: [
        { use: 'body.laneShip', config: { speed: 8.5, accel: 0, margin: 0, faceMotion: false } },
        playerGun(),
        { use: 'core.vitals', config: { lives: 3, hurtTicks: 24, respawnTicks: 45, invulnTicks: 75 } },
      ],
    },
    'captive-fighter': {
      role: 'a spare cyan arrowhead star-fighter of the same design as the player star-fighter',
      tags: [], size: { w: SHIP, h: SHIP }, render: { palette: 2, depth: 19, anchor: 'center', facing: 'symmetric' },
      modules: [
        { use: 'body.tether', config: { dx: 0, dy: -SHIP, clampToWorld: true, orphan: 'despawn' } },
        playerGun(),
        { use: 'rule.destructible', config: { hp: 1, score: 0, hitTicks: 0 } },
      ],
    },
    'boss-guardian': enemy('a large armoured green alien boss guardian with broad wings and a glowing beam emitter', 3, 2, 400, ['captor']),
    bee: enemy('a yellow alien bee fighter with a striped body and small swept wings', 4, 1, 100),
    moth: enemy('a red-and-blue alien moth fighter with broad patterned wings', 5, 1, 100),
    'tractor-beam': {
      role: 'a widening cone of pale green tractor-beam energy, brightest at its narrow top',
      tags: ['beam'], size: { w: 1.4, h: 3.2 }, render: { palette: 3, depth: 8, anchor: 'center', facing: 'symmetric' },
      modules: [
        { use: 'body.tether', config: { dx: 0, dy: 3.2, clampToWorld: false, orphan: 'despawn' } },
        { use: 'rule.telegraph', config: { warnTicks: 30, activeTag: 'tractor', lifeTicks: 90, warnAlpha: 0.35 } },
      ],
    },
    'player-shot': shot('a thin cyan laser bolt', 'player-fire', 2, 0.22, 0.5),
    'enemy-shot': shot('a small magenta plasma bolt', 'enemy-fire', 5, 0.26, 0.4),
  },
  globals,
  hud: [{ from: 'score', field: 'score' }, { from: 'core.vitals', field: 'lives' }, { from: 'flow.director', field: 'phase' },
    { from: 'flow.director', field: 'remaining' }, { from: 'flow.director', field: 'clock' }],
  input: { profile: 'lane-fire', labels: { a: 'FIRE' } },
  presentation: {
    projection: 'top-down', backgroundRole: 'a vertical deep-space starfield of pinpoint stars over a near-black void',
    palette: ['#05010f', '#1b2a6b', '#37d0ff', '#31e07a', '#ffd23f', '#ff3fa4', '#ff5a3c', '#ffffff'],
    backgroundPaletteIndex: 0,
  },
};

export const STOLEN_WINGS_TEMPLATE: TemplateV1 = {
  id: 'formation-shooter/stolen-wings',
  family: 'formation-shooter',
  title: 'Stolen Wings formation shooter',
  pitch: 'Vertical fixed shooter: a lane-locked fighter blasts a swooping swarm, frees captured ships for double firepower, clears five waves.',
  disclosure: {
    coreVerbs: ['move', 'fire'],
    mechanics: [
      'Your star-fighter is locked to a lane near the bottom of a tall starfield and moves only left and right, firing upward.',
      'Each wave swoops in along curved entry paths and settles into a swaying grid formation near the top.',
      'Bees and moths peel off the grid in weaving dives and fire down at you only while they dive on screen.',
      'Boss guardians head the formation and take two hits each.',
      'A diving boss guardian drops a tractor beam that flickers as a warning before it locks; a fighter caught in the live beam is captured, not destroyed, and your next fighter launches.',
      'Shoot the guardian carrying your captured fighter to free it: it docks beside you as a double fighter firing twin shots, a wider target. If the carrier escapes off the bottom, you lose a life.',
      'Every third wave is a no-fire bonus formation that loops the screen; destroy all of it for a 1000-point PERFECT bonus.',
      'Each wave dives faster and more often, and divers fire more frequently.',
    ],
    controls: 'Move left or right; fire shoots upward (both ships fire when docked as a double fighter).',
    winCondition: 'Clear wave 5, the flagship wave, to win; losing all three fighters is defeat.',
  },
  music: { mood: 'tense electric nostalgic space swarm', genre: 'retro arcade chiptune', tempoBpm: 140,
    instrumentation: ['square lead', 'pulse arpeggio', 'triangle bass', 'noise drums'] },
  spec: STOLEN_WINGS_SPEC,
  tunables: [
    { path: '/archetypes/star-fighter/modules/body.laneShip/speed', min: 6, max: 11 },
    { path: '/archetypes/star-fighter/modules/core.vitals/lives', min: 1, max: 5, integer: true },
    { path: '/archetypes/bee/modules/ai.diver/speed', min: 3.5, max: 7 },
    { path: '/archetypes/moth/modules/ai.diver/speed', min: 3.5, max: 7 },
    { path: '/archetypes/boss-guardian/modules/ai.diver/speed', min: 3.5, max: 7 },
    { path: '/archetypes/boss-guardian/modules/rule.destructible/hp', min: 2, max: 3, integer: true },
    { path: '/archetypes/tractor-beam/modules/rule.telegraph/warnTicks', min: 20, max: 40, integer: true },
    { path: '/globals/ai.captor/carryMul', min: 0.4, max: 0.9 },
    { path: '/globals/ai.formation/swayAmp', min: 0, max: 1.2 },
    { path: '/globals/flow.escalation/curves/0/rate', min: 1, max: 1.25 },
    { path: '/globals/flow.escalation/curves/1/base', min: 35, max: 75 },
    { path: '/globals/flow.director/interPhaseTicks', min: 30, max: 120, integer: true },
    { path: '/globals/flow.objective/count', min: 3, max: 5, integer: true },
  ],
  contentSlots: [
    { path: '/title', maxLength: 80 },
    { path: '/summary', maxLength: 160 },
    { path: '/archetypes/*/role', maxLength: 120 },
    { path: '/presentation/backgroundRole', maxLength: 120 },
  ],
  requirements: { events: ['fire', 'dive', 'hit', 'beam', 'capture', 'rescue', 'phase-clear', 'bonus-start',
    'destroy:bee', 'destroy:moth', 'destroy:boss-guardian'] },
  signatureModules: ['ai.formation', 'ai.diver', 'ai.captor', 'body.laneShip', 'rule.destructible'],
  // The fighter spawns mid-lane: steering moves it, firing launches a shot.
  // Freeing a captive needs a capture first, which the solver proof covers
  // (required `capture` and `rescue` events), not a fixed native script.
  verbProofs: [
    { verb: 'move', input: 'right', ms: 500, expect: { kind: 'state-change', path: 'seats.0.x' } },
    { verb: 'fire', input: 'a', ms: 300, expect: { kind: 'event', event: 'fire' } },
  ],
};
